import { Log, useTranslation } from '@fanfanlo';
import { Box, Button, Stack, Switch, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { SettingPanel } from './SettingPanel';
import { autoWebViewJs } from '@droid/android';
import _ from 'lodash';
import { NotificationSettingButton } from '../permissions/notification/NotificationSettingButton';

const fileLog = new Log(false, 'NotificationListenerPanel_file');
fileLog.childrenPaused = true;
fileLog.pause = true;
interface ISocketServerStatus {
  running: boolean;
  port: number;
  clientCount: number;
  socketEnabled: boolean;
  connectedClients: number;
}
const SERVER_CLIENT_NUM_UPDATED = 'serverClientNumUpdated';

function NotificationReadable() {
  const uiLog = fileLog.sub(false, '  NotificationReadable_ui');
  uiLog.pause = true;
  uiLog.childrenPaused = true;
  const { t } = useTranslation('homepage/components/notification-listener/content');
  const [readable, setReadable] = useState<boolean>(false);
  function refresh() {
    const fnLog = uiLog.sub(false, '    refresh_fn');
    fnLog.pause = true;
    fnLog.log('Checking if notification listener service is connected and readable');
    const script = `
        com.fanfanlo.lib.notification.MyNotificationListenerService.readIsConnected()
    `;
    const { javaResultData, javaResultString, errMsg } = autoWebViewJs.callScript<boolean>(script);
    fnLog.log(
      `Script result: javaResultData=${javaResultData}, javaResultString=${javaResultString}, errMsg=${errMsg}`,
    );
    if (errMsg) {
      setReadable(false);
      return;
    }
    setReadable(javaResultString === 'true');
  }
  useEffect(() => {
    refresh();
  });
  useEffect(() => {
    const effLog = uiLog.sub(false, '  useEffect_eventListener');
    effLog.pause = true
    function onConnected() {
      setReadable(true);
      effLog.log('onConnected_event');
    }
    function onDisconnected() {
      setReadable(false);
      effLog.log('onDisconnected_event');
    }
    effLog.log('Setting up event listeners for notification listener service connection status');
    const EVENT_TYPE_CONNECTED = 'notificationListenServiceConnected';
    const EVENT_TYPE_DISCONNECTED = 'notificationListenServiceDisconnected';
    const EVENT_TYPE_CONNECT_CHANGED = 'notificationListenServiceConnectChanged';
    window.addEventListener(EVENT_TYPE_CONNECTED, onConnected);
    window.addEventListener(EVENT_TYPE_DISCONNECTED, onDisconnected);
    return () => {
      window.removeEventListener(EVENT_TYPE_CONNECTED, onConnected);
      window.removeEventListener(EVENT_TYPE_DISCONNECTED, onDisconnected);
      effLog.log('Cleaned up event listeners for notification listener service connection status');
    };
  }, []);
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {t('NotificationListenerPanel.notificationReadable')}
        {readable
          ? t('NotificationListenerPanel.notificationReadableYes')
          : t('NotificationListenerPanel.notificationReadableNo')}
      </Typography>
      <NotificationSettingButton />
    </Stack>
  );
}

function SocketServerSwitch() {
  const uiLog = fileLog.sub(false, '  SocketServerSwitch_ui');
  const { t } = useTranslation('homepage/components/notification-listener/content');
  const [isRunning, setIsRunning] = useState<boolean>(false);

  function refresh() {
    const fnLog = uiLog.sub(false, '    refresh_fn');
    fnLog.log('Checking if socket server is running');
    const script = `
      com.fanfanlo.lib.notification.NotificationSocketServer.serverIsRunning()
    `;
    const { javaResultData, javaResultString, errMsg } = autoWebViewJs.callScript<boolean>(script);
    fnLog.log(
      `NotificationSocketServer.serverIsRunning Script result: javaResultData=${javaResultData}, javaResultString=${javaResultString}, errMsg=${errMsg}`,
    );
    if (errMsg) {
      setIsRunning(false);
      return;
    }
    setIsRunning(javaResultString === 'true');
  }

  function handleStart() {
    const fnLog = uiLog.sub(false, '    handleStart_fn');
    fnLog.log('Starting socket server');

    const notificationTitle = t('NotificationListenerPanel.foregroundService.title');
    const notificationContent = t('NotificationListenerPanel.foregroundService.content');
    const requestReason = t('NotificationListenerPanel.foregroundService.reason');

    const script = `
      var notificationTitle = "${notificationTitle.replace(/"/g, '\\"')}";
      var notificationContent = "${notificationContent.replace(/"/g, '\\"')}";
      var requestReason = ${JSON.stringify(requestReason)};

      com.fanfanlo.lib.notification.NotificationSocketServer.start(
        notificationTitle,
        notificationContent,
        requestReason
      );
    `;

    const result = autoWebViewJs.callScript(script);
    if (result.errMsg) {
      fnLog.error('Failed to start server:', result.errMsg);
      return;
    }
    fnLog.log('Server start request sent');
  }

  function handleStop() {
    const fnLog = uiLog.sub(false, '    handleStop_fn');
    fnLog.log('Stopping socket server');
    const script = 'com.fanfanlo.lib.notification.NotificationSocketServer.stop();';
    const result = autoWebViewJs.callScript(script);
    if (result.errMsg) {
      fnLog.error('Failed to stop server:', result.errMsg);
      return;
    }
    fnLog.log('Server stopped');
  }

  function handleToggle() {
    if (isRunning) {
      handleStop();
    } else {
      handleStart();
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    const effLog = uiLog.sub(false, '  useEffect_socketServerEvents');

    const EVENT_SWITCHED = 'notification_socket_server_switched';
    const EVENT_SWITCH_ON = 'notification_socket_server_switch_on';
    const EVENT_SWITCH_OFF = 'notification_socket_server_switch_off';

    function onSwitched(event: Event) {
      const customEvent = event as CustomEvent;
      const isOn = customEvent.detail === true || customEvent.detail?.data === true;
      effLog.log('notificationSocketServerSwitched Socket server switched:', isOn);
      setIsRunning(isOn);
    }

    function onSwitchOn() {
      effLog.log('notificationSocketServerSwitched Socket server switched on');
      setIsRunning(true);
    }

    function onSwitchOff() {
      effLog.log('notificationSocketServerSwitched Socket server switched off');
      setIsRunning(false);
    }

    effLog.log('Setting up event listeners for socket server status');
    window.addEventListener(EVENT_SWITCHED, onSwitched);
    window.addEventListener(EVENT_SWITCH_ON, onSwitchOn);
    window.addEventListener(EVENT_SWITCH_OFF, onSwitchOff);

    return () => {
      window.removeEventListener(EVENT_SWITCHED, onSwitched);
      window.removeEventListener(EVENT_SWITCH_ON, onSwitchOn);
      window.removeEventListener(EVENT_SWITCH_OFF, onSwitchOff);
      effLog.log('Cleaned up event listeners for socket server status');
    };
  }, []);

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {t('NotificationListenerPanel.socketServerStatus')}
        {isRunning
          ? t('NotificationListenerPanel.socketServerRunning')
          : t('NotificationListenerPanel.socketServerStopped')}
      </Typography>
      <Button variant="contained" color={isRunning ? 'error' : 'primary'} onClick={handleToggle} size="small" data-testid="SocketServerSwitch__toggleButton__01JG2B0086" data-test-comment="Socket服务器开关按钮，点击后启动或停止通知监听Socket服务器">
        {isRunning
          ? t('NotificationListenerPanel.socketServerStopButton')
          : t('NotificationListenerPanel.socketServerStartButton')}
      </Button>
    </Stack>
  );
}

export function NotificationListenerPanel() {
  const uiLog = fileLog.sub(false, '  NotificationListenerPanel_ui');
  const { t } = useTranslation('homepage/components/notification-listener/content');

  // 控制设置弹窗的显示和隐藏
  const [isSettingPanelOpen, setIsSettingPanelOpen] = useState(false);
  const [serverStatus, setServerStatus] = useState<ISocketServerStatus>({
    running: false,
    port: 0,
    clientCount: 0,
    socketEnabled: false,
    connectedClients: 0,
  });

  // 从 serverStatus 派生 isListenerEnabled 状态
  const isListenerEnabled = Boolean(serverStatus?.running);
  function readServerClientNum() {
    refreshServerStatus();
  }
  useEffect(() => {
    window.addEventListener(SERVER_CLIENT_NUM_UPDATED, readServerClientNum);
    return () => {
      window.removeEventListener(SERVER_CLIENT_NUM_UPDATED, readServerClientNum);
    };
  });
  function refreshServerStatus() {
    const script = `
    const s = com.fanfanlo.lib.notification.NotificationSocketServer.getStatus();
    s;`;
    const { javaResultData, javaResultString, errMsg } = autoWebViewJs.callScript<ISocketServerStatus>(script);
    if (errMsg) {
      uiLog.error('getStatus error:', errMsg);
      return;
    }
    uiLog.log('getStatus result:', javaResultString);
    const status = _.isObject(javaResultData)
      ? (javaResultData as ISocketServerStatus)
      : (JSON.parse(javaResultString || '{}') as ISocketServerStatus);
    setServerStatus(prev => ({
      ...prev,
      ...status,
      // 确保所有必要的字段都有默认值
      running: typeof status.running === 'boolean' ? status.running : prev.running,
      connectedClients: typeof status.connectedClients === 'number' ? status.connectedClients : prev.connectedClients,
    }));
  }
  useEffect(() => {
    refreshServerStatus();
  }, []);
  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    uiLog.log('handleSwitchChange_fn, checked:', event.target.checked);
    const checked = event.target.checked;

    // 立即更新本地 UI 状态以提供即时反馈
    setServerStatus(prev => ({
      ...prev,
      running: checked,
    }));

    try {
      if (checked) {
        // 启动服务 - 传递前台服务参数
        const notificationTitle = t('NotificationListenerPanel.foregroundService.title');
        const notificationContent = t('NotificationListenerPanel.foregroundService.content');
        const requestReason = t('NotificationListenerPanel.foregroundService.reason');

        uiLog.log('Starting notification listener with foreground service:', {
          notificationTitle,
          notificationContent,
          requestReason,
        });

        const script = `
          var notificationTitle = "${notificationTitle.replace(/"/g, '\\"')}";
          var notificationContent = "${notificationContent.replace(/"/g, '\\"')}";
          var requestReason = ${JSON.stringify(requestReason)};

          com.fanfanlo.lib.notification.NotificationSocketServer.start(
            notificationTitle,
            notificationContent,
            requestReason
          );
        `;

        const result = autoWebViewJs.callScript(script);

        if (result.errMsg) {
          throw new Error(result.errMsg);
        }

        uiLog.log('Foreground service request sent, waiting for user authorization');

        // 可选: 监听前台服务授权结果
        const eventName = 'notification_socket_server_foreground_event';
        const handleForegroundEvent = (event: Event) => {
          const customEvent = event as CustomEvent;
          const data = customEvent.detail;
          uiLog.log('Foreground service event:', data);

          if (data.status === 'SUCCESS') {
            uiLog.log('Foreground service authorized, server started');
            refreshServerStatus();
          } else if (data.status === 'ABORT') {
            uiLog.warn('User rejected foreground service request');
            // 回滚状态
            setServerStatus(prev => ({ ...prev, running: false }));
          } else if (data.status === 'FAILURE') {
            uiLog.error('Foreground service request failed');
            setServerStatus(prev => ({ ...prev, running: false }));
          }

          // 移除监听
          window.removeEventListener(eventName, handleForegroundEvent);
        };

        window.addEventListener(eventName, handleForegroundEvent);
      } else {
        // 停止服务
        const script = 'com.fanfanlo.lib.notification.NotificationSocketServer.stop();';
        const result = autoWebViewJs.callScript(script);

        if (result.errMsg) {
          throw new Error(result.errMsg);
        }

        setIsSettingPanelOpen(false);
      }
    } catch (error) {
      uiLog.error('Failed to toggle notification listener:', error);
      // 如果调用失败，回滚状态
      setServerStatus(prev => ({
        ...prev,
        running: !checked,
      }));
    }
  };

  const handleOpenSettingPanel = () => {
    uiLog.log('handleOpenSettingPanel_fn');
    setIsSettingPanelOpen(true);
  };

  const handleCloseSettingPanel = () => {
    uiLog.log('handleCloseSettingPanel_fn');
    setIsSettingPanelOpen(false);
  };

  interface INotificationListenerSetting {
    port: number;
    token: string;
  }
  return (
    <Stack
      direction="column"
      spacing={2}
      sx={{ width: '100%', border: '1px solid', borderColor: 'grey.300', borderRadius: '3px', p: 1 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
        <Typography variant="h6" sx={{ flexShrink: 0 }}>
          {t('NotificationListenerPanel.title')}
        </Typography>
        {/* 这个跟下面的开启服务器功能重叠，想要取消这个 */}
        {/* <Switch checked={isListenerEnabled} onChange={handleSwitchChange} inputProps={{ 'aria-label': 'controlled' }} /> */}

        <Button variant="outlined" onClick={handleOpenSettingPanel} data-testid="NotificationListenerPanel__settingsButton__01JG2B0087" data-test-comment="通知监听器设置按钮，点击后打开设置面板">
          {t('NotificationListenerPanel.settingsButtonLabel')}
        </Button>
        <SettingPanel open={isSettingPanelOpen} onClose={handleCloseSettingPanel} />
      </Stack>

      <Box>
        <NotificationReadable />
      </Box>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body1" sx={{}}>
          {t('NotificationListenerPanel.clientsNum')} {serverStatus.connectedClients}
        </Typography>
        <SocketServerSwitch />
      </Stack>
    </Stack>
  );
}
